'use client';

import React from 'react';
import {
  IconBrandGithub,
  IconBrandLinkedin,
  IconBrandX,
  IconSend,
  IconCheck,
} from '@tabler/icons-react';
import { useParams, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { motion } from 'framer-motion';

import { PageBreadcrumb } from '../page-breadcrumb';
import { PillButton } from '../pill-button';
import { AnimatedTooltip } from '@/components/ui/animated-tooltip';
import { jakarta } from '@/lib/fonts';
import { cn } from '@/lib/utils';

export function FormNextToSection({
  heading,
  sub_heading,
  form,
  section,
  social_media_icon_links,
}: {
  heading: string;
  sub_heading: string;
  form: any;
  section: any;
  social_media_icon_links?: any;
}) {
  const params = useParams();
  const searchParams = useSearchParams();
  const locale = (params?.locale as string) || 'en';
  const selectedService = searchParams?.get('service');
  const selectedTraining = searchParams?.get('training');

  const defaultMessageValue = selectedService
    ? (locale === 'fr'
        ? `Bonjour, je souhaite obtenir un devis pour le service suivant : ${selectedService}.`
        : `Hello, I would like to request a quote for the following service: ${selectedService}.`)
    : selectedTraining
    ? (locale === 'fr'
        ? `Bonjour, je souhaite obtenir des informations pour la formation suivante : ${selectedTraining}.`
        : `Hello, I would like to request information about the following training: ${selectedTraining}.`)
    : undefined;

  const socials = [
    {
      title: 'twitter',
      href: 'https://twitter.com/strapijs',
      icon: (
        <IconBrandX className="h-5 w-5 text-[#50456c] hover:text-[#c02029] dark:text-neutral-400 dark:hover:text-[#c02029] transition-colors" />
      ),
    },
    {
      title: 'github',
      href: 'https://github.com/strapi',
      icon: (
        <IconBrandGithub className="h-5 w-5 text-[#50456c] hover:text-[#c02029] dark:text-neutral-400 dark:hover:text-[#c02029] transition-colors" />
      ),
    },
    {
      title: 'linkedin',
      href: 'https://linkedin.com/strapi',
      icon: (
        <IconBrandLinkedin className="h-5 w-5 text-[#50456c] hover:text-[#c02029] dark:text-neutral-400 dark:hover:text-[#c02029] transition-colors" />
      ),
    },
  ];

  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-20 md:pb-28 bg-[#f4f2fc] dark:bg-neutral-900/90 border-b border-[#e5e2f5] dark:border-neutral-800/80">
      {/* Discreet background ambient lighting */}
      <div className="absolute inset-0 pointer-events-none -z-10 overflow-hidden">
        <div className="absolute top-1/2 -translate-y-1/2 right-0 w-[550px] h-[550px] rounded-full blur-[150px] opacity-[0.04] bg-[#c02029]" />
        <div className="absolute bottom-10 left-0 w-[500px] h-[500px] rounded-full blur-[150px] opacity-[0.035] bg-[#c02029]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Streamlined Breadcrumb Header */}
        <div className="mb-8">
          <PageBreadcrumb
            locale={locale}
            items={[{ label: 'Contact' }]}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7 p-6 sm:p-10 md:p-12 rounded-3xl bg-white dark:bg-neutral-900 border border-[#e5e2f5] dark:border-neutral-800 shadow-xl shadow-[#301754]/5"
          >
            <h1
              className={cn(
                jakarta.className,
                'text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-[#2e1452] dark:text-white mb-4 leading-tight'
              )}
            >
              {heading}
            </h1>
            <p className="text-base sm:text-lg text-[#3b2d56] dark:text-neutral-300 font-medium leading-relaxed mb-8">
              {sub_heading}
            </p>

            <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
              {form?.inputs?.map((input: any, index: number) => (
                <div key={`form-input-${index}`}>
                  {input.type !== 'submit' && (
                    <label
                      htmlFor={input.type === 'textarea' ? 'message' : `input-${index}`}
                      className="block text-sm font-semibold text-[#2e1452] dark:text-neutral-200 mb-2"
                    >
                      {input.name}
                    </label>
                  )}

                  <div>
                    {input.type === 'textarea' ? (
                      <textarea
                        rows={5}
                        id="message"
                        placeholder={input.placeholder}
                        defaultValue={defaultMessageValue}
                        className="block w-full bg-[#fbfafc] dark:bg-neutral-950 px-4 py-3 rounded-2xl border border-[#e5e2f5] dark:border-neutral-800 text-[#2e1452] dark:text-neutral-100 placeholder:text-neutral-400 focus:ring-2 focus:ring-[#c02029]/30 focus:border-[#c02029] focus:outline-none text-base transition-colors shadow-sm"
                      />
                    ) : input.type === 'submit' ? (
                      <div className="pt-4">
                        <PillButton
                          type="submit"
                          className="w-full justify-between"
                          text={input.name || (locale === 'fr' ? 'Envoyer le message' : 'Send message')}
                          icon={<IconSend className="w-5 h-5 stroke-[2.2] transition-transform duration-300 group-hover:translate-x-0.5" />}
                        />
                      </div>
                    ) : (
                      <input
                        id={`input-${index}`}
                        type={input.type}
                        placeholder={input.placeholder}
                        className="block w-full bg-[#fbfafc] dark:bg-neutral-950 px-4 py-3 rounded-2xl border border-[#e5e2f5] dark:border-neutral-800 text-[#2e1452] dark:text-neutral-100 placeholder:text-neutral-400 focus:ring-2 focus:ring-[#c02029]/30 focus:border-[#c02029] focus:outline-none text-base transition-colors shadow-sm"
                      />
                    )}
                  </div>
                </div>
              ))}
            </form>

            <div className="flex items-center space-x-6 pt-8 mt-8 border-t border-[#e5e2f5] dark:border-neutral-800">
              <span className="text-xs font-semibold text-[#50456c] dark:text-neutral-400 uppercase tracking-wider">
                {locale === 'fr' ? 'Réseaux Sociaux' : 'Connect with us'}
              </span>
              <div className="flex items-center space-x-4">
                {socials.map((social) => (
                  <Link
                    href={social.href}
                    target="_blank"
                    key={social.title}
                    className="p-2 rounded-full hover:bg-[#c02029]/10 transition-colors"
                  >
                    {social.icon}
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Section Info Card */}
          {section && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="lg:col-span-5 relative group"
            >
              {/* Discreet ambient glow behind card */}
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-[#c02029]/15 via-rose-500/10 to-transparent blur-xl opacity-30 group-hover:opacity-60 transition duration-500 pointer-events-none" />

              <div className="relative p-6 sm:p-10 rounded-3xl bg-white dark:bg-neutral-900 border border-[#e5e2f5] dark:border-neutral-800 shadow-xl shadow-[#301754]/5">
                {section.users && section.users.length > 0 && (
                  <div className="flex flex-row items-center justify-start mb-8">
                    <AnimatedTooltip items={section.users} />
                  </div>
                )}

                <h2
                  className={cn(
                    jakarta.className,
                    'text-2xl sm:text-3xl font-bold tracking-tight text-[#2e1452] dark:text-white mb-4 leading-snug'
                  )}
                >
                  {section.heading}
                </h2>

                <p className="text-base text-[#5e5873] dark:text-neutral-300 leading-relaxed mb-6">
                  {section.sub_heading}
                </p>

                <div className="space-y-3 pt-6 border-t border-[#e5e2f5] dark:border-neutral-800">
                  <div className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-[#c02029]/10 text-[#c02029] flex items-center justify-center flex-shrink-0">
                      <IconCheck className="w-3.5 h-3.5 stroke-[2.75]" />
                    </span>
                    <span className="text-sm font-medium text-[#3b2d56] dark:text-neutral-200">
                      {locale === 'fr' ? 'Réponse sous 24 heures ouvrées' : 'Response within 24 business hours'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-[#c02029]/10 text-[#c02029] flex items-center justify-center flex-shrink-0">
                      <IconCheck className="w-3.5 h-3.5 stroke-[2.75]" />
                    </span>
                    <span className="text-sm font-medium text-[#3b2d56] dark:text-neutral-200">
                      {locale === 'fr' ? 'Accompagnement expert sur-mesure' : 'Tailored expert engineering & advice'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-[#c02029]/10 text-[#c02029] flex items-center justify-center flex-shrink-0">
                      <IconCheck className="w-3.5 h-3.5 stroke-[2.75]" />
                    </span>
                    <span className="text-sm font-medium text-[#3b2d56] dark:text-neutral-200">
                      {locale === 'fr' ? 'Confidentialité & RGPD garantis' : 'Strict NDA and data security standards'}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

export default FormNextToSection;
